import { Pipe, PipeTransform } from '@angular/core';

// The preview is one ellipsised line, so only the start of a message is ever
// visible. Stripping the whole of a long reply would be wasted work on every
// render of the conversation list.
const MAX_SOURCE_CHARS = 600;

/**
 * Reduces markdown to the words a reader would see, for one-line previews.
 *
 * Deliberately not built on `marked`: the full `markdown` pipe renders HTML,
 * and a preview needs plain text, not HTML with the tags stripped. It is also
 * testable here, where jest stubs `marked` out (apps/cpd/jest.config.ts).
 */
export function markdownToPlainText(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  return (
    value
      .slice(0, MAX_SOURCE_CHARS)
      // Block structure first, while line starts still mean something.
      .replace(/```[^\n]*/g, ' ')
      .replace(/^\s{0,3}(?:[-*_]\s*){3,}$/gm, ' ')
      .replace(/^\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)*\|?\s*$/gm, ' ')
      .replace(/^\s{0,3}#{1,6}\s+/gm, '')
      .replace(/^\s{0,3}>\s?/gm, '')
      .replace(/^\s*(?:[-*+]|\d+[.)])\s+/gm, '')
      // Inline markup. Links and images keep their text, the URL goes.
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
      .replace(/\[([^\]]+)\]\([^)]*$/, '$1')
      .replace(/<[^>\n]+>/g, ' ')
      .replace(/(\*\*|__)(?!\s)(.+?)(?<!\s)\1/g, '$2')
      // Single-character emphasis only at word edges, so snake_case and 2*3*4
      // survive.
      .replace(/(^|[^\w*])\*(?!\s)([^*\n]+?)(?<!\s)\*(?![\w*])/gm, '$1$2')
      .replace(/(^|\W)_(?!\s)([^_\n]+?)(?<!\s)_(?!\w)/gm, '$1$2')
      .replace(/~~(.+?)~~/g, '$1')
      .replace(/`([^`]*)`/g, '$1')
      // Whatever a truncation left unpaired, and table cell borders.
      .replace(/\*\*|__|~~|`/g, '')
      .replace(/\s*\|\s*/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

@Pipe({
  name: 'markdownPreview'
})
export class MarkdownPreviewPipe implements PipeTransform {
  public transform(value: string | null | undefined): string {
    return markdownToPlainText(value);
  }
}
